
import Vue from 'vue';
import { initializeStore } from '@seamless/store';
import { connectionName, isWithinVehicleContext } from '@dh-io-globalelem/vehicle-data-store-connection';
import { MediaQueryService, mobileBreakpoints, WB_RESIZE_EVENT_NAME } from '@owc-utils/MediaQueryService';
import { ButtonType } from '@owc-types/Button.enum';
import { BaseCtaLink } from '@owc-types/BaseCtaLink';
import { LinkTarget } from '@owc-types/LinkTarget.enum';
import { CtaButtonsDisplayMethod, ScenarioKeys } from '@owc-utils/AutomatedCta/CtaAutomation.enum';
import {
  StageCtaButtonsComputed,
  StageCtaButtonsData,
  StageCtaButtonsMethods,
  StageCtaButtonsProps,
} from './StageCtaButtons.d';
import { setCtaButtonsFromService } from '@owc-utils/AutomatedCta/CtaService';
import { ctaItems } from '@owc-utils/AutomatedCta/CtaAutomation';

/**
 * A CTA button mainly used in the stage component.
 * Provides the user with the possibility to do somethig based on a given context.
 */
export default Vue.extend<
  StageCtaButtonsData,
  StageCtaButtonsMethods,
  StageCtaButtonsComputed,
  StageCtaButtonsProps
>({
  name: 'owc-stage-cta-buttons',
  props: {
    buttons: {
      type: Array as () => Array<BaseCtaLink>,
      required: true,
    },
    cta: {
      type: Object,
      required: false,
    },
    isDarkMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    vehicleData: {
      type: Object,
      required: false,
    },
    scenario: {
      type: String,
      required: false,
    },
  },
  computed: {
    isAutomatedDisplayMethod() {
      return this.cta?.ctaDisplayMethod === CtaButtonsDisplayMethod.Automate;
    },
    shouldRenderSkeletons() {
      return this.isAutomatedDisplayMethod && !this.ctas.length;
    },
  },
  data() {
    return {
      wbMediaQueryService: new MediaQueryService(window),
      isMobile: true,
      ctas: [],
    };
  },
  async mounted() {
    // Used to change image ratio given a MediaQuery Event
    window.addEventListener(
      WB_RESIZE_EVENT_NAME,
      this.resizeEventHandler as EventListenerOrEventListenerObject,
    );
    this.wbMediaQueryService.observe(true);
    await this.setCtaButtons();
  },
  beforeDestroy() {
    window.removeEventListener(
      WB_RESIZE_EVENT_NAME,
      this.resizeEventHandler as EventListenerOrEventListenerObject,
    );
  },
  watch: {
    cta() {
      this.setCtaButtons();
    },
  },
  methods: {
    resizeEventHandler(resizeEvent) {
      this.isMobile = mobileBreakpoints.includes(resizeEvent.detail.current);
    },
    async setCtaButtons() {
      if (this.isAutomatedDisplayMethod) {
        if ((await isWithinVehicleContext()) && !this.vehicleData?.scoped) {
          this.setCtaButtonsFromStore();
        } else {
          const ctaData = await setCtaButtonsFromService(
            this.vehicleData,
            this.cta,
            this.scenario as ScenarioKeys,
          );
          if (ctaData?.primary) {
            this.ctas = this.mapToCtas(ctaData?.primary, ctaData?.secondary);
          }
        }
      } else {
        this.setCtaButtonsFromProps();
      }
    },
    setCtaButtonsFromStore() {
      const store = initializeStore();
      store.subscribe(connectionName, state => {
        this.$nextTick(() => {
          const { primary, secondary }: ctaItems = state.cta;
          this.ctas = this.mapToCtas(primary, secondary);
        });
      });
    },
    setCtaButtonsFromProps() {
      const primaryButton = this.buttons.find(({ type }) => type === ButtonType.Primary);
      const secondaryButton = this.buttons.find(({ type }) => type === ButtonType.Secondary);
      // Due to workbench a primary button must to be on the right
      this.ctas = [secondaryButton, primaryButton].filter(cta => cta) as BaseCtaLink[];
    },
    mapToCtas(primary, secondary) {
      const commonProps = {
        target: LinkTarget.Self,
      };

      const buttons = [
        {
          ...primary,
          ...commonProps,
          type: ButtonType.Primary,
        },
      ];

      if (secondary) {
        buttons.unshift({
          ...secondary,
          ...commonProps,
          type: ButtonType.Secondary,
        });
      }

      return buttons;
    },
    getCtaButtonClasses(cta) {
      if (cta.type === ButtonType.Primary) {
        return 'wb-button--primary owc-stage-cta-buttons__button--primary';
      }
      return 'wb-button--secondary owc-stage-cta-buttons__button--secondary';
    },
  },
});
